// src/utils/openaiService.js

/**
 * OpenAI Service for generating explanations
 * Provides AI-powered explanations for incorrect answers
 */

import { fetchExplanationFromGROQ } from "./groqService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

/**
 * Generate explanation for an incorrect answer using OpenAI
 * @param {number} questionId - The question ID
 * @param {string} question - The question text
 * @param {string} studentAnswer - The student's answer
 * @param {string} correctAnswer - The correct answer
 * @returns {Promise<string>} - The explanation
 */
export const generateExplanation = async (
  questionId,
  question,
  studentAnswer,
  correctAnswer
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/en/student/ai-explanation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        questionId,
        question,
        studentAnswer,
        correctAnswer,
      }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.explanation || "Unable to generate explanation";
  } catch (error) {
    console.error("Error generating explanation:", error);
    throw error;
  }
};

/**
 * Generate explanation with retry logic
 * @param {number} questionId - The question ID
 * @param {string} question - The question text
 * @param {string} studentAnswer - The student's answer
 * @param {string} correctAnswer - The correct answer
 * @param {number} maxRetries - Maximum number of retries (default: 3)
 * @returns {Promise<string>} - The explanation
 */

export const generateExplanationWithRetry = async (
  questionId,
  question,
  studentAnswer,
  correctAnswer,
  maxRetries = 3
) => {
  // First try GROQ lookup (fast, returns curated explanations if available)
  try {
    const groqExplanation = await fetchExplanationFromGROQ(question);
    if (groqExplanation) return groqExplanation;
  } catch (err) {
    // Ignore and fallback to AI generation
    console.warn("GROQ lookup failed, falling back to AI generation", err);
  }

  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const explanation = await generateExplanation(
        questionId,
        question,
        studentAnswer,
        correctAnswer
      );
      return explanation;
    } catch (error) {
      lastError = error;
      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries) {
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, attempt - 1) * 1000)
        );
      }
    }
  }

  throw lastError || new Error("Failed to generate explanation after retries");
};

export default generateExplanationWithRetry;
